import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography } from "@mui/material/";
import LoadingButton from "@mui/lab/LoadingButton";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import SendIcon from "@mui/icons-material/Send";

import actions from "../../Store/actions";
import styles from "./styles";
import {
    useToaster,
    useAuditProgressDetails,
    useGroupedAudits,
    useGroupAuditProgressDetails,
} from "../../common/hooks";
import { logToSentry } from "../../config/Sentry";
import { Loading, ActionBar } from "../../common/components";
import SignatureSteps from "./SignatureSteps";
import useAuditUpload from "./useAuditUpload";
import UploadProgress from "./UploadProgress";
import GroupAuditAlert from "./GroupAuditAlert";

const AuditSubmission = () => {
    const { auditId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useToaster();

    const [loading, setLoading] = useState(false);
    const [captureSignatures, setCaptureSignatures] = useState(false);

    const { auditUpload, typesList } = useSelector((state) => state);
    const { isComplete } = useAuditProgressDetails("db", auditId);

    const { groupedRequired } = useGroupAuditProgressDetails("db", auditId);
    const { totalAssetToSync, syncProgressAssets, linkedAudits, uploading } =
        auditUpload;
    const groupedAudit = useGroupedAudits();
    const {
        generateAndUploadJSON,
        uploadProgress,
        uploadMessage,
        showDownload,
        downloadUrl,
    } = useAuditUpload();

    /** Check if isSignature required for the audit  */
    const isSignatureRequired = useMemo(() => {
        const { organisationSettings, settings } = typesList;
        const signature =
            settings &&
            settings?.data?.find((item) => item.name === "signature required");

        const signVal =
            organisationSettings &&
            organisationSettings?.data?.find(
                (item) => item.settingId === signature.id
            );

        return signVal && signVal.value.length > 0 ? signVal.value : "false";
    }, [typesList]);

    const groupAudit = useMemo(() => {
        return groupedAudit.map((item) => ({
            auditId: item.audit.id,
            locationName: item.location.name,
        }));
    }, [groupedAudit]);

    /** If no signature required  disable the siganture capture validation*/
    useEffect(() => {
        if (isSignatureRequired === "false") {
            setCaptureSignatures(true);
        }
    }, [isSignatureRequired]);

    const onSubmit = async () => {
        try {
            setLoading(true);

            /** Check if there are any linked audits present for
             * partial submission of assets
             */
            if (linkedAudits.length > 0) {
                for (const id of linkedAudits) {
                    await dispatch(
                        actions.auditUpload.create.partialSubmitRequest(
                            id,
                            null
                        )
                    );
                }
            }

            await generateAndUploadJSON(auditId);
            toast.success(t("messages.uploadSuccessful"));
            // Wait for 2 seconds after success message
            await new Promise((resolve) => setTimeout(resolve, 2000));

            dispatch(actions.auditUpload.create.postClear());

            // TODO: Clear auditSignature for the audit
            await dispatch(
                actions.auditUpload.create.clearSignatureRequest([auditId])
            );

            navigate(`/auth/audits/list`);
        } catch (error) {
            console.log(error);
            toast.error(t("errors.auditUploadPost"));
            logToSentry("onSubmit:auditUploadPost", {
                error,
            });
        }
    };

    const onSubmitGroup = async () => {
        try {
            setLoading(true);

            //Store auditIds to clear signature
            const groupAuditIds = groupedAudit.map((item) => item.audit.id);

            // If there are any stale audit submit the data
            // 1. Use for of loop because it does not miss any request
            // 2. No audit submission gets skipped because of a single failure
            // 3. Try catch if linked audit submission creates an issue
            if (linkedAudits.length > 0) {
                for (const id of linkedAudits) {
                    try {
                        await dispatch(
                            actions.auditUpload.create.partialSubmitRequest(
                                id,
                                null
                            )
                        );
                    } catch (error) {
                        logToSentry(`Failed to submit linked audit ${id}:`);
                        console.error(
                            `Failed to submit linked audit ${id}:`,
                            error
                        );
                    }
                }
            }

            for (const auditId of groupAuditIds) {
                try {
                    const isLastItem =
                        auditId === groupAuditIds[groupAuditIds.length - 1];

                    await generateAndUploadJSON(auditId);
                    await dispatch(
                        actions.auditUpload.create.groupAuditSubmissionClear(
                            auditId,
                            isLastItem // Pass `true` only for the last item
                        )
                    );
                } catch (error) {
                    logToSentry(
                        `For of loop:Failed to submit audit ${auditId}:`
                    );
                    console.error(`Failed to submit audit ${auditId}:`, error);
                    throw error;
                }
            }

            toast.success(t("messages.uploadSuccessful"));
            // Wait for 2 seconds after success message
            await new Promise((resolve) => setTimeout(resolve, 2000));

            // Clear auditSignature for the audit
            await dispatch(
                actions.auditUpload.create.clearSignatureRequest(groupAuditIds)
            );

            navigate(`/auth/audits/list`);
        } catch (error) {
            console.error(error);
            toast.error(t("errors.groupUploadPost"));
            logToSentry("onSubmitGroup:groupUploadPost", {
                error,
            });
        }
    };

    if (loading) {
        return (
            <UploadProgress
                showDownload={showDownload}
                uploadMessage={uploadMessage}
                uploadProgress={uploadProgress}
                downloadUrl={downloadUrl}
                auditId={auditId}
            />
        );
    }

    return (
        <>
            {uploading && (
                <Loading
                    loadingText="messages.uploadMessage"
                    loadingParams={{
                        totalAssetToSync,
                        syncProgressAssets,
                    }}
                />
            )}

            <Box sx={styles.auditSubmissionContainer}>
                {isSignatureRequired === "true" &&
                !captureSignatures &&
                !uploading ? (
                    <Typography component="div" align="center" my={2}>
                        {!loading && (
                            <SignatureSteps
                                captureSignatures={captureSignatures}
                                setCaptureSignatures={setCaptureSignatures}
                            />
                        )}
                    </Typography>
                ) : (
                    <>
                        {groupedAudit.length > 0 && groupedRequired === 0 ? (
                            <GroupAuditAlert groupAudit={groupAudit} />
                        ) : (
                            <Typography component="p" align="center">
                                {t("messages.auditSubmit")}
                            </Typography>
                        )}
                    </>
                )}
                <ActionBar>
                    <Grid container spacing={2}>
                        {groupedAudit.length > 0 && groupedRequired === 0 ? (
                            <Grid item xs={12}>
                                <LoadingButton
                                    fullWidth
                                    disabled={
                                        loading ||
                                        uploading ||
                                        !captureSignatures
                                    }
                                    loading={loading}
                                    loadingPosition="end"
                                    endIcon={<GroupWorkIcon />}
                                    onClick={onSubmitGroup}
                                    variant={"contained"}
                                >
                                    {t("submitGroup")}
                                </LoadingButton>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <LoadingButton
                                    fullWidth
                                    disabled={
                                        loading ||
                                        uploading ||
                                        !captureSignatures
                                    }
                                    loading={loading}
                                    loadingPosition="end"
                                    endIcon={<SendIcon />}
                                    onClick={onSubmit}
                                    variant={
                                        isComplete ? "contained" : "outlined"
                                    }
                                >
                                    {t("submitAudit")}
                                </LoadingButton>
                            </Grid>
                        )}
                    </Grid>
                </ActionBar>
            </Box>
        </>
    );
};

export default AuditSubmission;

//TODO
//1. Check if this required for future
/* {t(isComplete ? "submitAudit"  : "submitAudit" // submitPartialAudit)} */
